import { v4 as uuidv4 } from 'uuid';
import { Integration } from "../types"; // Import Node and Edge types

// --- CSV Import Types ---
export interface ScriptInfo {
    url: string;
    sizeKb: number;
    loadTimeMs: number;
}

export interface IdentityInfo {
    name: string;
    providerName: string;
    value: string;
}

export interface NodeData {
    [key: string]: any;
    type: string;
    label: string;
    project?: string;
    customLabel?: string;
}

// --- CSV Import Settings & Mappings ---
export const DEFAULT_CLUSTER_NAME = "MetaRouter Streaming Cluster";
export const CLUSTER_SETTINGS = {
    "consent-enforcement": true,
    "bot-detection": false,
    "data-replay": false,
    "schema-enforcement": true,
    "transformations": true,
};
export const CLUSTER_TEAM_ACCESS = "direct"; // 'direct' means off

// Refined map focusing on known destinations from schema + common patterns
export const URL_TO_INTEGRATION_MAP: { [key: string]: string } = {
    // Direct Name Matches (from schema)
    'connect.facebook.net': 'Facebook Pixel', 
    's.pinimg.com': 'Pinterest CAPI',
    'ct.pinterest.com': 'Pinterest CAPI', 
    'bat.bing.com': 'Microsoft Advertising', 
    'googleadservices.com': 'Google Ads',
    'google.com/ads/conversion/': 'Google Ads',
    'google.com/ads/ga-audiences': 'Google Ads',
    'googletagmanager.com/gtag/js': 'Google Analytics',
    'google-analytics.com/analytics.js': 'Google Analytics',
    'analytics.tiktok.com': 'TikTok', 
    'ads.twitter.com': 'X Ads', 
    'ads-api.twitter.com': 'X Ads',
    'snap.licdn.com': 'LinkedIn Insight Tag',
    'ads.linkedin.com': 'LinkedIn Insight Tag',
    'sdk.snapkit.com': 'Snapchat', 
    'sc-static.net/scevent.min.js': 'Snapchat', 
    'www.redditstatic.com/ads/pixel.js': 'Reddit Ads', 
    'adsrvr.org': 'The Trade Desk', 
    'criteo.net': 'Criteo',
    'adnxs.com': 'Xandr',
    'doubleclick.net': 'Google Campaign Manager 360', 
    'ads.yahoo.com': 'Yahoo DSP',

    // Other potential mappings (confirm names in schema if possible)
    'cdn.amplitude.com': 'Amplitude', // c2b3e2e2...
    'cdn.mxpnl.com': 'Mixpanel', // 2ca79be5...
    'static.klaviyo.com': 'Klaviyo', // Need ID/confirmation
    'assets.adobedtm.com': 'Adobe Experience Platform Launch', // Need ID/confirmation
    'try.abtasty.com': 'AB Tasty', // Need ID/confirmation
    'siteintercept.qualtrics.com': 'Qualtrics', // Need ID/confirmation
    'service.force.com/embeddedservice': 'Salesforce Service Cloud', // Need ID/confirmation (Likely not a destination)
    'd.la13-core1.sfdc-yfeipo.salesforceliveagent.com': 'Salesforce Service Cloud', // Need ID/confirmation (Likely not a destination)
    'cdns.gigya.com': 'SAP Customer Data Cloud (Gigya)', // Need ID/confirmation
    'cdn.branch.io': 'Branch', // Need ID/confirmation

    // Consent Tools (Not destinations, but useful for suggestion)
    'cdn.cookielaw.org': 'OneTrust',
    'consent.cookiebot.com': 'Cookiebot',
    '*.onetrust.com': 'OneTrust', // Using wildcard notation for clarity
    '*.quantcast.com': 'Quantcast Choice',
    '*.trustarc.com': 'TrustArc',
    '*.usercentrics.eu': 'Usercentrics',

    // Added based on metarouter-audit-4f.com.pl.csv analysis (confirm actual integration names)
    'googletagmanager.com/gtm.js': 'Google Tag Manager', // Reverted GTM match to specific path
    'prefixbox.com': 'Prefixbox', // Added for Prefixbox
    'salesmanago.pl': 'Salesmanago', // Added for Salesmanago
    'trustmate.io': 'Trustmate', // Added for Trustmate
};

export const CONSENT_TOOL_SUGGESTIONS: { [key: string]: string } = {
    'cdn.cookielaw.org': 'OneTrust',
    'consent.cookiebot.com': 'Cookiebot',
    'app.termly.io': 'Termly',
    'cdn.iubenda.com': 'iubenda',
    '*.quantcast.com': 'Quantcast Choice',
    '*.trustarc.com': 'TrustArc',
    '*.onetrust.com': 'OneTrust',
    '*.usercentrics.eu': 'Usercentrics',
};

// Map for known script filenames to descriptive names
const KNOWN_SCRIPT_NAME_MAP: { [key: string]: string } = {
    // 'gtm.js': 'Google Tag Manager', // Removed: GTM should only be matched as a destination via URL_TO_INTEGRATION_MAP
    'analytics.js': 'Google Analytics (Legacy UA)',
    // 'pixel.js': 'Tracking Pixel', // Too generic, could be many things
    'iframe_api': 'YouTube IFrame Player API', // Often part of the path
    'www-embed-player.js': 'YouTube Embedded Player',
    'js.cookie.min.js': 'js-cookie Library',
    'fbevents.js': 'Facebook Pixel', // Add specific known scripts
};

// Updated list of script filenames/patterns to truly ignore
const IGNORED_SCRIPT_PATTERNS = [
    'index.js', // Still potentially too generic
    'embed.js',
    'base.js',
    'cast_sender.js',
    '_r',
    'popups.js',
    'endscreen.js',
    'captions.js',
    // Generic patterns that might indicate non-tracking scripts
    'chunk-',
    'vendor.',
    'runtime.',
    'polyfills.',
    'framework.',
];


// --- CSV Helper Functions ---

// Helper function to read file as text
export const readFileAsText = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => resolve(event.target?.result as string);
    reader.onerror = (error) => reject(error);
    reader.readAsText(file);
  });
};

// Refined findIntegrationForUrl with known script mapping and ignore list
export function findIntegrationForUrl(url: string, integrations: Integration[]): Integration | null {
    const urlLower = url.toLowerCase();
    console.log(`\n[findIntegrationForUrl] Checking URL: "${url}"`);

    // --- Step 1: Check Direct Destination Pattern Matches ---
    console.log(`[findIntegrationForUrl] --- Step 1: Checking URL_TO_INTEGRATION_MAP (Destinations) ---`);
    for (const pattern in URL_TO_INTEGRATION_MAP) {
        // Handle simple wildcards (like *.domain.com)
        const isWildcard = pattern.startsWith('*.');
        const domainToCheck = isWildcard ? pattern.substring(2) : pattern;
        const urlMatchesPattern = isWildcard
            ? urlLower.endsWith(domainToCheck.toLowerCase())
            : urlLower.includes(domainToCheck.toLowerCase());

        // console.log(`[findIntegrationForUrl]   Comparing with pattern: "${pattern}" (Domain: "${domainToCheck}", Wildcard: ${isWildcard}) -> Matches: ${urlMatchesPattern}`); // Verbose logging

        if (urlMatchesPattern) {
            const integrationName = URL_TO_INTEGRATION_MAP[pattern];
            console.log(`[findIntegrationForUrl]   Pattern matched! Looking for integration named: "${integrationName}" (must be destination)`);

            // --- Add specific GTM logging ---
            if (integrationName === 'Google Tag Manager') {
                console.log(`[findIntegrationForUrl]   >>> GTM pattern matched for URL: "${url}". Searching integrations list...`);
            }
            // --- End GTM logging ---

            // Find the integration by name, ensuring it's a destination
            const found = integrations.find(int =>
                int.name.toLowerCase() === integrationName.toLowerCase() && int.destination
            );

             // --- Add specific GTM logging ---
            if (integrationName === 'Google Tag Manager') {
                if (found) {
                    console.log(`[findIntegrationForUrl]   >>> GTM Found in integrations: ID=${found.id}, Name=${found.name}, Destination=${found.destination}`);
                } else {
                    // Check if it exists but destination is false
                    const gtmIntegrationExists = integrations.find(int => int.name.toLowerCase() === integrationName.toLowerCase());
                    if (gtmIntegrationExists) {
                         console.log(`[findIntegrationForUrl]   >>> GTM Found in integrations BUT Destination is FALSE: ID=${gtmIntegrationExists.id}, Name=${gtmIntegrationExists.name}, Destination=${gtmIntegrationExists.destination}`);
                    } else {
                        console.log(`[findIntegrationForUrl]   >>> GTM NOT Found in integrations list at all.`);
                    }
                }
            }
             // --- End GTM logging ---

            if (found) {
                console.log(`[findIntegrationForUrl]   SUCCESS (Step 1): Found destination integration "${found.name}" (ID: ${found.id}) via pattern "${pattern}".`);
                return found; // Return the actual destination integration
            } else {
                 console.log(`[findIntegrationForUrl]   INFO (Step 1): Integration named "${integrationName}" found via pattern, but it's either not loaded or not marked as a destination.`);
                 // Do not return here; continue checking other possibilities like known scripts or fallbacks
            }
        }
    }
    console.log(`[findIntegrationForUrl] --- Step 1 Finished ---`);


    // --- Step 2: Check Known Scripts (Non-Destinations) & Ignore List ---
    console.log(`[findIntegrationForUrl] --- Step 2: Checking Known Scripts & Ignore List ---`);
    try {
        const parsedUrl = new URL(url);
        const pathnameLower = parsedUrl.pathname.toLowerCase();
        const filename = pathnameLower.substring(pathnameLower.lastIndexOf('/') + 1);

        // Check Known Scripts Map first
        if (filename && KNOWN_SCRIPT_NAME_MAP[filename]) {
            const knownName = KNOWN_SCRIPT_NAME_MAP[filename];
            console.log(`[findIntegrationForUrl] KNOWN SCRIPT: Matched filename "${filename}" to known script "${knownName}".`);
            // Return a placeholder Integration object matching the type definition
            return {
                id: `known-script-${filename.replace(/[^a-z0-9]/gi, '-')}`, // Generate a stable ID
                name: knownName,
                description: `Identified known script: ${knownName}`,
                category: 'Utility', // Assign a default category
                logo_url: '', // Optional field
                keywords: [], // Optional field
                enabled: false, // Required field - default to false
                destination: false, // Required field - explicitly false for known scripts
                sync_injector: false, // Required field - default to false
                display_order: 999, // Required field - place at the end
                created_at: new Date().toISOString(), // Optional field - use correct name
                updated_at: new Date().toISOString(), // Optional field - use correct name

            };
        }
         // Check if pathname contains a known script pattern (like iframe_api)
        const knownPatternMatch = Object.keys(KNOWN_SCRIPT_NAME_MAP).find(pattern => pathnameLower.includes(pattern));
        if (knownPatternMatch) {
             const knownName = KNOWN_SCRIPT_NAME_MAP[knownPatternMatch];
             console.log(`[findIntegrationForUrl] KNOWN SCRIPT: Matched pattern "${knownPatternMatch}" in path to known script "${knownName}".`);
             // Return a placeholder Integration object matching the type definition
             return {
                 id: `known-script-${knownPatternMatch.replace(/[^a-z0-9]/gi, '-')}`,
                 name: knownName,
                 description: `Identified known script: ${knownName}`,
                 category: 'Utility',
                 logo_url: '',
                 keywords: [],
                 enabled: false,
                 destination: false,
                 sync_injector: false,
                 display_order: 999,
                 created_at: new Date().toISOString(),
                 updated_at: new Date().toISOString(),
             };
        }


        // Check Ignore List if not a known script
        if (filename && IGNORED_SCRIPT_PATTERNS.includes(filename)) {
            console.log(`[findIntegrationForUrl] IGNORING: URL's filename "${filename}" is in the ignore list.`);
            return null;
        }
        // Check if URL contains any ignored pattern
        if (IGNORED_SCRIPT_PATTERNS.some(ignoredPattern => urlLower.includes(ignoredPattern))) {
             console.log(`[findIntegrationForUrl] IGNORING: URL "${url}" contains an ignored pattern.`);
             return null;
        }

    } catch (e) {
        console.warn(`[findIntegrationForUrl] Could not parse URL "${url}". Proceeding without filename/ignore check.`);
    }
    // --- End Known Script & Ignore Check ---


    // 2. Fallback: Check if any integration keyword exists in the URL
    console.log(`[findIntegrationForUrl] --- Step 2: Checking Integration Keywords ---`);
    for (const integration of integrations) {
        if (!integration.destination) {
            // console.log(`[findIntegrationForUrl]   Skipping "${integration.name}" (not a destination).`); // Optional: Log skipped non-destinations
            continue;
        }
        if (!integration.keywords || integration.keywords.length === 0) {
            // console.log(`[findIntegrationForUrl]   Skipping "${integration.name}" (no keywords).`); // Optional: Log skipped integrations with no keywords
            continue;
        }

        console.log(`[findIntegrationForUrl]   Checking keywords for "${integration.name}": [${integration.keywords.join(', ')}]`);
        const matchingKeyword = integration.keywords.find(kw => kw && urlLower.includes(kw.toLowerCase()));

        if (matchingKeyword) {
            console.log(`[findIntegrationForUrl]   SUCCESS: Found destination integration "${integration.name}" (ID: ${integration.id}) via keyword "${matchingKeyword}".`);
            return integration;
        }
    }
     console.log(`[findIntegrationForUrl] --- Step 2 Finished ---`);

    // 3. Fallback: Check if URL contains parts of the integration name (less reliable)
    console.log(`[findIntegrationForUrl] --- Step 3: Checking Integration Name Parts ---`);
    for (const integration of integrations) {
        if (!integration.destination) {
            // console.log(`[findIntegrationForUrl]   Skipping "${integration.name}" (not a destination).`); // Optional: Log skipped non-destinations
            continue;
        }
        const nameParts = integration.name.toLowerCase().split(/[\s-]+/);
        console.log(`[findIntegrationForUrl]   Checking name parts for "${integration.name}": [${nameParts.join(', ')}]`);
        const matchingPart = nameParts.find(part => part.length > 3 && urlLower.includes(part)); // Require part length > 3

        if (matchingPart) {
            console.log(`[findIntegrationForUrl]   SUCCESS: Found destination integration "${integration.name}" (ID: ${integration.id}) via name part "${matchingPart}".`);
            return integration;
        }
    }
    console.log(`[findIntegrationForUrl] --- Step 3 Finished ---`);


    console.log(`[findIntegrationForUrl] FAILURE: No match found for URL "${url}"`); // Log failure
    return null;
}

export function suggestConsentTool(scripts: ScriptInfo[]): string {
    const urls = scripts.map(s => s.url.toLowerCase());
    for (const url of urls) {
        for (const pattern in CONSENT_TOOL_SUGGESTIONS) {
             // Handle simple wildcards
            const isWildcard = pattern.startsWith('*.');
            const domainToCheck = isWildcard ? pattern.substring(2) : pattern;
            const urlMatchesPattern = isWildcard
                ? url.endsWith(domainToCheck.toLowerCase())
                : url.includes(domainToCheck.toLowerCase());

            if (urlMatchesPattern) {
                return CONSENT_TOOL_SUGGESTIONS[pattern];
            }
        }
    }
    return "";
}

export function generateNodeId(type: string): string {
    // Use uuid for better uniqueness in browser context
    return `${type}-${uuidv4()}`;
}

export function generateEdgeId(sourceId: string, targetId: string): string {
    return `edge-${sourceId}-${targetId}`;
}

export function formatFileSize(kb: number): string {
    if (kb < 1) return `${Math.round(kb * 1024)} B`;
    if (kb < 1024) return `${kb.toFixed(1)} kB`;
    return `${(kb / 1024).toFixed(1)} MB`;
}

// Basic CSV line parser
export const parseCsvLine = (line: string): string[] => {
    // Basic parser: handles simple commas and quoted fields (doesn't handle escaped quotes within quotes)
    const parts: string[] = [];
    let currentPart = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
        const char = line[i];
        if (char === '"') {
            inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
            parts.push(currentPart.trim());
            currentPart = '';
        } else {
            currentPart += char;
        }
    }
    parts.push(currentPart.trim()); // Add the last part
    return parts.map(p => p.replace(/^"|"$/g, '')); // Remove surrounding quotes if any
};
